/**
 * t232u: standard Web Push for the phone (PWA + public/sw.js + VAPID). iOS only allows it for Home Screen apps
 * (iOS 16.4+), so on an iPhone in Safari the Me panel shows how to add The Hub to the Home Screen instead.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
// t257u: a push tap counts as opened directly, so a chat's Back goes to the chat list, not the page underneath.
import { DIRECT_NAV_STATE } from '@/mobile/useSmartBack';

export type PushUiState = 'unsupported' | 'ios-install' | 'denied' | 'ready';

export interface PushEnv {
  ios: boolean;
  standalone: boolean;
  /** serviceWorker + PushManager + Notification all present. */
  apis: boolean;
  permission: NotificationPermission | 'unsupported';
}

/** What the Notifications row should show. Pure, so it is unit tested. */
export function pushUiState(e: PushEnv): PushUiState {
  if (e.ios && !e.standalone) return 'ios-install';
  if (!e.apis || e.permission === 'unsupported') return 'unsupported';
  if (e.permission === 'denied') return 'denied';
  return 'ready';
}

export function isIos(ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints ?? 0): boolean {
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
}

export function isStandalone(): boolean {
  return (navigator as Navigator & { standalone?: boolean }).standalone === true
    || (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches);
}

export function readPushEnv(): PushEnv {
  const apis = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  return { ios: isIos(), standalone: isStandalone(), apis, permission: apis ? Notification.permission : 'unsupported' };
}

/** VAPID public key (base64url) -> the bytes PushManager.subscribe wants. */
export function urlBase64ToUint8Array(b64: string): Uint8Array {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return Promise.resolve(null);
  return navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => null);
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration('/')) ?? registerServiceWorker();
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

function keysOf(sub: PushSubscription) {
  const j = sub.toJSON() as { endpoint: string; keys?: { p256dh?: string; auth?: string } };
  return { endpoint: j.endpoint, p256dh: j.keys?.p256dh ?? '', auth: j.keys?.auth ?? '' };
}

/** Asks for permission (must run from a tap), subscribes this browser and registers it with the API. */
export async function enablePush(publicKey: string): Promise<'on' | 'denied' | 'unsupported'> {
  const env = readPushEnv();
  if (!env.apis) return 'unsupported';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'unsupported';
  const reg = await registration();
  if (!reg) return 'unsupported';
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with another key (keys rotated) can't be used: start again.
  const key = urlBase64ToUint8Array(publicKey);
  const old = sub?.options.applicationServerKey;
  if (sub && old && !sameBytes(new Uint8Array(old), key)) { await sub.unsubscribe().catch(() => undefined); sub = null; }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key as BufferSource });
  await api.pushSubscribe(keysOf(sub));
  return 'on';
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

/** On only when this browser has a subscription the server still knows (it is dropped when a push service says it's gone). */
export async function isPushOn(): Promise<boolean> {
  const sub = await currentSubscription().catch(() => null);
  if (!sub || readPushEnv().permission !== 'granted') return false;
  return (await api.pushStatus(sub.endpoint).catch(() => ({ subscribed: false }))).subscribed;
}

export async function sendTestPush() {
  const sub = await currentSubscription();
  return api.pushTest(sub?.endpoint);
}

function sameBytes(a: Uint8Array, b: Uint8Array) { return a.length === b.length && a.every((x, i) => x === b[i]); }

/** Tapping a notification while the app is open: the service worker asks the page to route in place. */
export function useServiceWorkerNavigate() {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const on = (e: MessageEvent) => {
      const d = e.data as { type?: string; url?: string } | null;
      if (d?.type === 'hub:navigate' && typeof d.url === 'string' && d.url.startsWith('/') && !d.url.startsWith('//')) navigate(d.url, { state: DIRECT_NAV_STATE });
    };
    navigator.serviceWorker.addEventListener('message', on);
    return () => navigator.serviceWorker.removeEventListener('message', on);
  }, [navigate]);
}

export function ServiceWorkerNavigate() { useServiceWorkerNavigate(); return null; }
